// /case-study — index of per-service detailed dossiers (not blog posts).
import { createFileRoute, Link } from "@tanstack/react-router";
import { ALL_DOSSIERS } from "@/data/service-dossiers";
import ConsultantAdvisoryBlock from "@/components/ConsultantAdvisoryBlock";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { canonicalAndHreflang } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "กรณีศึกษารายบริการ — ขั้นตอนจริง เอกสาร และข้อควรระวัง";
const DESC =
  "รวมกรณีศึกษาเชิงลึกรายบริการ: Notary Public, รับรองกรมการกงสุล, NAATI, หนังสือรับรองความประพฤติ, จดทะเบียนสมรสต่างประเทศ และอื่น ๆ พร้อมเอกสารที่ต้องใช้และลำดับขั้นตอน";

export const Route = createFileRoute("/case-study/")({
  loader: async () => {
    const { ALL_DOSSIERS } = await import("@/data/service-dossiers");
    return { items: ALL_DOSSIERS.map((d) => ({ slug: d.slug, title: d.title })) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/case-study` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: canonicalAndHreflang("/case-study"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "@id": `${SITE}/case-study#page`,
          name: TITLE,
          description: DESC,
          url: `${SITE}/case-study`,
          mainEntity: {
            "@type": "ItemList",
            itemListElement: (loaderData?.items ?? []).map((d, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: d.title,
              url: `${SITE}/case-study/${d.slug}`,
            })),
          },
        }),
      },
    ],
  }),
  component: CaseStudyIndex,
});

function CaseStudyIndex() {
  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <nav aria-label="breadcrumb" className="text-sm text-slate-500">
        <Link to="/" className="hover:underline">
          หน้าแรก
        </Link>{" "}
        / <span className="text-navy-deep">กรณีศึกษา</span>
      </nav>

      <h1 className="mt-3 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">
        กรณีศึกษารายบริการ
      </h1>
      <p className="mt-3 max-w-3xl text-slate-700">
        แต่ละหน้าอธิบายเคสจริงหนึ่งเส้นทาง ตั้งแต่เอกสารที่ต้องเตรียม ลำดับหน่วยงานที่ต้องผ่าน
        ระยะเวลาโดยประมาณ จุดที่เคสมักถูกตีกลับ ไปจนถึงคำถามที่ลูกค้าถามบ่อยที่สุด
        หากคุณไม่อยากเดินเรื่องเอง ทีมงานของเราดูแลให้ครบทั้งกระบวนการ
      </p>

      <ul className="mt-8 grid gap-4 sm:grid-cols-2">
        {ALL_DOSSIERS.map((d) => (
          <li key={d.slug}>
            <Link
              to="/case-study/$slug"
              params={{ slug: d.slug }}
              className="block h-full rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-gold/60 hover:shadow-md"
            >
              <span className="text-xs font-semibold uppercase tracking-wide text-amber-700">
                {d.cluster}
              </span>
              <h2 className="mt-1 font-display text-lg font-bold text-navy-deep">{d.title}</h2>
              <p className="mt-2 line-clamp-3 text-sm text-slate-600">{d.answer}</p>
              <p className="mt-3 text-xs text-slate-500">
                {d.steps.length} ขั้นตอน · เอกสาร {d.documents.length} รายการ · คำถาม {d.faqs.length} ข้อ
              </p>
            </Link>
          </li>
        ))}
      </ul>

      <div className="mt-10">
        <ConsultantAdvisoryBlock locale="th" />
      </div>
      <PlanningToolsLinks locale="th" className="mt-8" />
    </main>
  );
}
